// Modal component to edit a logbook entry.
//
// props.task is the entry (CompletedTask) to edit.
// props.item is the Item the entry belongs to.
// props.logbook is the Logbook the entry belongs to (selects the field set /
//   type via logbook.logbookType). When omitted, defaults to Maintenance so
//   legacy callers keep working.
// props.save is an async function called with the merged, split entry
//   ({ ...columnFields, fields: {...jsonFields}, id, itemId, logbookId }).
// props.deleteEntry is an async function called (with the entry id) on delete.
// props.show / props.onHide control visibility.

import ModalEditForm from '../../components/ModalEditForm';
import { validateRequiredName } from '../../utilities/Validators.mjs';
import {
  deriveEntryFields,
  fieldsForType,
  flattenEntry,
  splitEntryFields,
  LOGBOOK_TYPE_MAINTENANCE,
} from '../../fleet-shared/LogbookTypes.mjs';
import * as Database from '../../utilities/Database.mjs';
import { tripSuggestionsFromEntries, withTripSuggestions } from '../../utilities/tripSuggestions';
import { withStartCounterSuggestion } from '../../utilities/counterSuggestion';
import { usePreviousEndCounter } from './usePreviousEndCounter';

function EditLogbookEntry({ show, onHide, item, logbook, task, save, deleteEntry }) {
  const typeId = logbook?.logbookType || LOGBOOK_TYPE_MAINTENANCE;

  // Build the field list for this logbook type. Clone each field so we never
  // mutate the shared registry, and inject the "Counter Based On" select
  // options (Maintenance only) from the item's counters at runtime.
  function buildTaskFields() {
    const fields = fieldsForType(typeId).map((f) => ({ ...f }));

    for (let i = 0; i < fields.length; i++) {
      if ('itemCounterId' === fields[i].key) {
        const counterItems = item?.itemCounter?.items ?? [];
        fields[i].selects = counterItems.map((counterItem, index) => {
          // index + 1 to make room for no-counter, set below.
          return { id: counterItem.id, index: index + 1, name: counterItem.name };
        });
        // Allow selecting no-counter.
        fields[i].selects.unshift({ id: null, index: 0, name: '(None)' });
        break;
      }
    }

    return fields;
  }

  // The generic EditForm edits a flat object; hoist json-backed fields to the
  // top level for editing, then split them back on save.
  const flat = task ? flattenEntry(task, typeId) : null;

  async function handleSave(edited) {
    // `preserve` matters even though this form knows nothing about routes: an
    // AWSJSON write replaces the whole blob, so without the entry's current
    // one, editing Hours here would delete the entry's geo route.
    const split = splitEntryFields(edited, typeId, { preserve: task.fields });
    // Preserve identity/relationship keys the form doesn't carry.
    split.id = task.id;
    split.itemId = task.itemId;
    if (task.logbookId) {
      split.logbookId = task.logbookId;
    }
    await save(split);
  }

  async function handleDelete() {
    await deleteEntry(task.id);
    onHide();
  }

  // The logbook's existing trips, for the Trip Name box's menu -- this modal is
  // where a fresh entry is most often filed under one, so it needs the menu as
  // much as the detail page does. Lazy, and never fatal: an unreadable list
  // leaves a plain text box, which is what this field was before.
  const loadTripSuggestions = logbook?.id
    ? () => Database.listCompletedTasksForLogbook(logbook.id)
      .then((entries) => tripSuggestionsFromEntries(entries, typeId))
    : null;
  // The previous entry's End Counter, offered on the Start Counter row -- the
  // same suggestion the detail page shows, through the same hook, so the two
  // surfaces cannot answer differently.
  //
  // Gated on `show`. This component is mounted permanently by Logbook.jsx and
  // LogbookSummary.jsx with `show` toggled, so an ungated read would go out
  // for whichever entry was last edited every time one of those lists renders.
  const startCounterHint = usePreviousEndCounter({
    logbookId: logbook?.id,
    entry: task,
    typeId: typeId,
    enabled: show,
  });
  const fields = withStartCounterSuggestion(
    withTripSuggestions(buildTaskFields(), loadTripSuggestions),
    startCounterHint
  );
  // Only Maintenance-style types carry a `name` column; hold an emptied one
  // invalid instead of autosaving it (the schema requires it).
  const validate = fields.some((f) => f.key === 'name') ? validateRequiredName : undefined;

  return (
    (task != null) &&
    <ModalEditForm
      show={show}
      onHide={onHide}
      fields={fields}
      item={flat}
      save={handleSave}
      delete={deleteEntry ? handleDelete : undefined}
      deriveState={(previous, next) => deriveEntryFields(previous, next, typeId)}
      validate={validate}
      recordKeyPrefix="entry"
    />
  );
}

export default EditLogbookEntry;
